import { z } from "zod";
// The ISOMORPHIC half, deliberately. Both names are pure, but
// `execution-boundary.js` also MINTS boundaries with `node:crypto`/`node:os`, and
// this module is reachable from the client entry (surfaces/console.js →
// authority.js), so importing them from there put a node builtin in the browser
// graph and the production client bundle could not be built at all.
import { isDerivedExecutionBoundaryId, isSameExecutionBoundary, } from "./execution-boundary-identity.js";
import { protocolIdSchema, protocolTimestampSchema, } from "./identifiers.js";
export const actorRoleSchema = z.enum([
    "requester",
    "compiler",
    "worker",
    "collector",
    "verifier",
    "reviewer",
    "enforcer",
    "learner",
    "system",
]);
export const authorityCapabilitySchema = z.enum([
    "work:create",
    "contract:compile",
    "contract:accept",
    "contract:amend:propose",
    "contract:amend:decide",
    "attempt:execute",
    "evidence:submit",
    "evidence:admit",
    "determination:record",
    "receipt:issue",
    "review:human-check",
    "review:request",
    "review:resolve",
    "review:override",
    "enforcement:decide",
    "effect:apply",
    "learning:propose",
    "canary:record",
]);
export const authenticatedActorSchema = z
    .object({
    actorId: protocolIdSchema,
    tenantId: protocolIdSchema,
    roles: z.array(actorRoleSchema).min(1),
    capabilities: z.array(authorityCapabilitySchema),
    credentialId: protocolIdSchema,
    executionBoundaryId: protocolIdSchema,
    authenticatedAt: protocolTimestampSchema,
})
    .strict();
export const actorIdentitySchema = authenticatedActorSchema
    .pick({
    actorId: true,
    tenantId: true,
    roles: true,
    executionBoundaryId: true,
})
    .strict();
export function actorIdentity(actor) {
    return actorIdentitySchema.parse({
        actorId: actor.actorId,
        tenantId: actor.tenantId,
        roles: actor.roles,
        executionBoundaryId: actor.executionBoundaryId,
    });
}
export const independenceAssessmentSchema = z
    .object({
    classification: z.enum([
        "independent",
        "self_attested",
        "shared_boundary",
        "unknown",
    ]),
    actorDistinct: z.boolean(),
    boundaryDistinct: z.boolean(),
    custodyDistinct: z.boolean(),
    basis: z.array(z.string().min(1)).min(1),
    assessedByActorId: protocolIdSchema,
})
    .strict();
export class VerificationAuthorityError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.name = "VerificationAuthorityError";
        this.code = code;
    }
}
export function requireTenant(actor, tenantId) {
    if (actor.tenantId !== tenantId) {
        throw new VerificationAuthorityError("tenant-mismatch", `Actor ${actor.actorId} is not authenticated for tenant ${tenantId}.`);
    }
}
export function requireCapability(actor, capability) {
    if (!actor.capabilities.includes(capability)) {
        throw new VerificationAuthorityError("missing-capability", `Actor ${actor.actorId} lacks ${capability}.`);
    }
}
export function requireIndependentContractAcceptance(input) {
    requireTenant(input.accepter, input.tenantId);
    requireCapability(input.accepter, "contract:accept");
    if (input.accepter.actorId === input.workerActorId) {
        throw new VerificationAuthorityError("worker-self-acceptance", "The worker cannot accept the contract for its own attempt.");
    }
}
export function requireIndependentVerifier(input) {
    requireTenant(input.verifier, input.tenantId);
    requireTenant(input.worker, input.tenantId);
    requireCapability(input.verifier, "determination:record");
    requireCapability(input.verifier, "receipt:issue");
    if (input.verifier.actorId === input.worker.actorId) {
        throw new VerificationAuthorityError("worker-self-verification", "The worker cannot verify its own attempt.");
    }
    if (input.verifier.executionBoundaryId === input.worker.executionBoundaryId) {
        throw new VerificationAuthorityError("shared-execution-boundary", "The verifier must not share the worker's mutable execution boundary.");
    }
}
/**
 * True when two execution boundaries are distinguishable as UNITS OF EXECUTION
 * rather than as strings.
 *
 * `independenceAxes` below decides boundary distinctness by `!==`. That is the
 * right shape for boundaries whose namespace means something, and it is worth
 * nothing for boundaries whose namespace does not: two labels minted by ONE
 * process differ as strings and share every fact a boundary is supposed to
 * assert. This is not hypothetical. The one real production coding-work receipt
 * was "independently collected" by
 * `excellent:real-work-collector @ real-work-subprocess:33033:7d03f7220d4cc4d9`
 * against producer `worktree:d2-payload:33033:7d03f7220d4cc4d9` — same PID,
 * same suffix, different prefix — and classified `independent` on all three
 * axes.
 *
 * `execution-boundary.ts`'s `assertRecordableExecutionBoundary` already states
 * the rule this predicate enforces: values outside the `boundary:proc:*`
 * namespace "are not trusted for independence anyway". Nothing consulted that
 * sentence. Grepping a rule's DEFINITION proves it was written; only grepping
 * its READERS proves it runs. This is the reader.
 *
 * `isDerivedExecutionBoundaryId` is used, never WIDENED: the `boundary:proc:*`
 * namespace is exactly the one whose writes are guarded, so an id in it was
 * either derived by the process that owns it or observed by a supervisor that
 * watched the unit of execution from outside. One OS process can hold exactly
 * one derived id, so this predicate cannot be satisfied by relabelling.
 *
 * WHAT IT DOES NOT ESTABLISH, stated plainly and inherited unchanged from the
 * boundary axis it rests on: this is PROCESS-distinct, not PARTY-distinct. A
 * party that can run two processes can hold both ends. It closes relabelling
 * inside one process, which is the class that was live.
 *
 * Deliberately a SEPARATE predicate rather than a change to `independenceAxes`.
 * Tightening the shared axes would re-classify every non-derived boundary in
 * the fleet at once — measured cost, recorded by lane OBS: the whole
 * fixture-backed gate family (`scripts/lib/universal-verification-fixture.mjs`
 * mints `boundary:worker` / `boundary:collector`) would turn every
 * `requiresIndependentCollection` obligation UNKNOWN. That is a fleet decision,
 * not a lane one. Applied here to the one determination whose subject the
 * producer could otherwise choose.
 */
export function structurallyDistinctBoundaries(left, right) {
    return (isDerivedExecutionBoundaryId(left) &&
        isDerivedExecutionBoundaryId(right) &&
        !isSameExecutionBoundary(left, right));
}
export function independenceAxes(input) {
    const actorDistinct = input.collector.collectorActorId !== input.producerActorId;
    const boundaryDistinct = input.collector.collectorBoundaryId !== input.producerBoundaryId;
    const custodyDistinct = input.collector.custodyBoundaryId !== input.producerBoundaryId;
    return {
        actorDistinct,
        boundaryDistinct,
        custodyDistinct,
        classification: actorDistinct && boundaryDistinct && custodyDistinct
            ? "independent"
            : !actorDistinct
                ? "self_attested"
                : !boundaryDistinct || !custodyDistinct
                    ? "shared_boundary"
                    : "unknown",
        basis: [
            `collector:${actorDistinct ? "distinct" : "producer"}`,
            `execution-boundary:${boundaryDistinct ? "distinct" : "shared"}`,
            `custody:${custodyDistinct ? "distinct" : "producer-controlled"}`,
        ],
    };
}
export function deriveIndependence(input) {
    const { actorDistinct, boundaryDistinct, custodyDistinct, classification, basis } = independenceAxes({
        producerActorId: input.producerActorId,
        producerBoundaryId: input.producerBoundaryId,
        collector: {
            collectorActorId: input.collector.actorId,
            collectorBoundaryId: input.collector.executionBoundaryId,
            custodyBoundaryId: input.custodyBoundaryId,
        },
    });
    return independenceAssessmentSchema.parse({
        classification,
        actorDistinct,
        boundaryDistinct,
        custodyDistinct,
        basis,
        assessedByActorId: input.assessedBy.actorId,
    });
}
